import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { FileUp, Loader2, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ImportAttendeesCard({ eventId, token }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [rowCount, setRowCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const lines = text.split('\n').filter(l => l.trim());
    setFileName(f.name);
    setCsvText(text);
    setRowCount(Math.max(0, lines.length - 1));
    setResult(null);
  };

  const runImport = async () => {
    if (!csvText) return;
    setImporting(true);
    try {
      const r = await axios.post(`${API}/events/${eventId}/import-attendees`,
        { csv_text: csvText },
        { headers: { Authorization: `Bearer ${token}` } });
      setResult(r.data);
      toast.success(`Imported ${r.data.imported} tickets for ${r.data.orders} buyers`);
      setCsvText(''); setFileName(''); setRowCount(0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  return (
    <Card className="border-border/60" data-testid="import-attendees-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FileUp className="h-4 w-4 text-amber-400" />
          <h3 className="font-semibold text-sm">Import attendees from Eventbrite</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Upload the attendee CSV export — each guest gets a real QR ticket here, and their
          original Eventbrite barcode scans at the door too. Re-uploading skips duplicates.
        </p>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="import-attendees-file" />
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-attendees-choose">
            <Upload className="h-4 w-4 mr-1.5" /> {fileName ? fileName.slice(0, 32) : 'Choose CSV file'}
          </Button>
          {csvText && (
            <Button size="sm" disabled={importing} onClick={runImport}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white"
              data-testid="import-attendees-run">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              Import {rowCount} rows
            </Button>
          )}
        </div>
        {result && (
          <p className="text-xs text-emerald-400" data-testid="import-attendees-result">
            ✓ {result.imported} tickets imported · {result.orders} buyers
            {result.skipped_existing > 0 ? ` · ${result.skipped_existing} already imported (skipped)` : ''}
            {result.bad_rows > 0 ? ` · ${result.bad_rows} rows missing an email` : ''}
            {' '}— use “Email tickets to all” to send everyone their QR link.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
